import { ApplicationStatus } from 'api/btlApplicationForm/types';
import { CaseSidebarItemId, CaseSidebarSection, StatusSidebarChange } from '../types';

/**
 * OO happy path: NEW → APPLICATION_SUBMITTED (post DIP submit) → DIP_ACCEPTED → …
 *
 * Editability (see isOoCaseEditableStatus):
 *   NEW — editable (pages available at APPLICATION_SUBMITTED)
 *   APPLICATION_SUBMITTED — read-only
 *   DIP_ACCEPTED — editable when canEditDipAcceptedSections is on
 *
 * NEW
 *   APPLICATION: Submit DIP (when oo_case_submission; hidden while HPP affordability is available
 *   and not passed)
 *   OTHER: Communication (Notes)
 *
 * APPLICATION_SUBMITTED (and mapped post-DIP-submit / pre-DIP-accepted statuses)
 *   DOCUMENTS: Customer files, Evidence
 *   APPLICATION: Bank details (Submit DIP removed)
 *   OTHER: Communication
 *   (Financing, Applicants, Household always visible; Product/Affordability when HPP permissioned)
 *
 * DIP_ACCEPTED (and later mapped pre-fees statuses)
 *   APPLICATION: Property, Solicitors, Submit application (hidden while HPP affordability not passed)
 *
 * FEES_PAID / REVIEWING_APPLICATION / OFFER_ISSUED / OFFER_EXPIRED
 *   same as DIP_ACCEPTED + Valuation; Submit application removed
 *
 * OFFER_ACCEPTED / APPLICATION_DECLINED / COMPLETION
 *   same as FEES_PAID + Post Offer
 *
 * Overview is always shown separately (like BTL Case Summary).
 * Financing, Applicants and Household are always visible in APPLICATION.
 */

export const OO_STATUS_CHAIN: ApplicationStatus[] = [
  ApplicationStatus.NEW,
  ApplicationStatus.APPLICATION_SUBMITTED,
  ApplicationStatus.DIP_ACCEPTED,
  ApplicationStatus.FEES_PAID,
  ApplicationStatus.OFFER_ACCEPTED,
];

export const OO_STATUS_SIDEBAR_CHANGES: Partial<Record<ApplicationStatus, StatusSidebarChange>> = {
  [ApplicationStatus.NEW]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.SUBMIT_DIP],
      [CaseSidebarSection.OTHER]: [CaseSidebarItemId.COMMUNICATION],
    },
  },
  [ApplicationStatus.APPLICATION_SUBMITTED]: {
    add: {
      [CaseSidebarSection.DOCUMENTS]: [CaseSidebarItemId.CUSTOMER_FILES, CaseSidebarItemId.EVIDENCE],
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.BANK_DETAILS],
    },
    remove: [CaseSidebarItemId.SUBMIT_DIP],
  },
  [ApplicationStatus.DIP_ACCEPTED]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [
        CaseSidebarItemId.PROPERTY,
        CaseSidebarItemId.SOLICITORS,
        CaseSidebarItemId.SUBMIT_APPLICATION,
      ],
    },
  },
  [ApplicationStatus.FEES_PAID]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.VALUATION],
    },
    remove: [CaseSidebarItemId.SUBMIT_APPLICATION],
  },
  [ApplicationStatus.OFFER_ACCEPTED]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.POST_OFFER],
    },
  },
};

export const OO_STATUS_TO_CHAIN_STATUS: Partial<Record<ApplicationStatus, ApplicationStatus>> = {
  [ApplicationStatus.DIP_SUBMITTED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.REVIEWING_DIP]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_REFERRED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_DECLINED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_EXPIRED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.BTL_APPLICATION_SUBMITTED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.RECOMMENDED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PAYMENT_DUE]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.PROPERTY_DECLINED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_ACCEPTED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.AWAITING_PROPERTY]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PROPERTY_SUBMITTED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.BOOKING_PRE_ADVICE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRE_ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRODUCT_NOT_SUITABLE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.REVIEWING_APPLICATION]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_ISSUED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_EXPIRED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_BEING_REVISED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.APPLICATION_DECLINED]: ApplicationStatus.OFFER_ACCEPTED,
  [ApplicationStatus.COMPLETION]: ApplicationStatus.OFFER_ACCEPTED,
};

export function isAfterOoDipAccepted(status?: string): boolean {
  if (!status) {
    return false;
  }

  const chainStatus = OO_STATUS_TO_CHAIN_STATUS[status as ApplicationStatus] ?? (status as ApplicationStatus);
  const dipAcceptedChainIndex = OO_STATUS_CHAIN.indexOf(ApplicationStatus.DIP_ACCEPTED);
  const currentChainIndex = OO_STATUS_CHAIN.indexOf(chainStatus);

  if (currentChainIndex === -1 || dipAcceptedChainIndex === -1) {
    return false;
  }

  return currentChainIndex > dipAcceptedChainIndex;
}

/** OO statuses after DIP accepted (excluding exact DIP_ACCEPTED) until fees are paid. */
export const OO_POST_DIP_PRE_FEES_STATUSES = [
  ApplicationStatus.DIP_ACCEPTED,
  ApplicationStatus.AWAITING_PROPERTY,
  ApplicationStatus.PROPERTY_SUBMITTED,
  ApplicationStatus.BOOKING_PRE_ADVICE,
  ApplicationStatus.PRE_ADVICE_BOOKED,
  ApplicationStatus.ADVICE_BOOKED,
  ApplicationStatus.RECOMMENDED,
  ApplicationStatus.PRODUCT_NOT_SUITABLE,
] as const;

export function isOoPostDipPreFeesStatus(status?: string): boolean {
  if (!status) {
    return false;
  }

  return (OO_POST_DIP_PRE_FEES_STATUSES as readonly string[]).includes(status);
}

/**
 * OO form editability (mirrors BTL NEW / DIP_ACCEPTED pattern):
 * - NEW (or missing status): editable — same pages as APPLICATION_SUBMITTED visibility
 * - DIP_ACCEPTED: editable when canEditDipAcceptedSections is enabled
 * - APPLICATION_SUBMITTED and all other statuses: not editable
 */
export function isOoCaseEditableStatus(status?: string, canEditDipAcceptedSections?: boolean): boolean {
  if (!status || status === ApplicationStatus.NEW) {
    return true;
  }

  if (status === ApplicationStatus.DIP_ACCEPTED) {
    return Boolean(canEditDipAcceptedSections);
  }

  return false;
}

export function isOoProductEditableStatus(status?: string, canEditDipAcceptedSections?: boolean): boolean {
  return isOoCaseEditableStatus(status, canEditDipAcceptedSections);
}
